import React, { useState, useEffect } from "react";
import { Box, Popover, TextField, Typography } from "@mui/material";
import { ChatBubbleOutline, Star, StarBorder } from "@mui/icons-material";
import { orange } from "@mui/material/colors";
import { ITaskRating } from "../ts/Definitions";

const DIFFICULTY_EMOJIS = ["😊", "😅", "😰", "😓", "🥵"];
const FUN_EMOJIS = ["😞", "😐", "🙂", "😄", "🤩"];

/** Returns the average of fun + frequency (1–5), or undefined if neither is set. */
export function calcTaskRatingAvg(r: ITaskRating): number | undefined {
  const vals = [r.fun, r.frequency].filter((v): v is number => v !== undefined);
  return vals.length > 0 ? vals.reduce((a, b) => a + b, 0) / vals.length : undefined;
}

/** Aggregate an array of individual ratings into per-dimension averages and combined notes. */
export function aggregateTaskRatings(ratings: ITaskRating[]): ITaskRating {
  const result: ITaskRating = {};
  for (const dim of ["difficulty", "fun", "frequency"] as (keyof ITaskRating)[]) {
    const vals = ratings.map(r => r[dim]).filter((v): v is number => v !== undefined);
    if (vals.length > 0) result[dim] = vals.reduce((a, b) => a + b, 0) / vals.length;
  }
  const notes = ratings.map(r => r.note?.trim()).filter((n): n is string => !!n);
  if (notes.length > 0) result.note = notes.join("\n");
  return result;
}

// ── Dimension row ─────────────────────────────────────────────────────────────

interface DimRowProps {
  label: string;
  sublabel?: string;
  value?: number;
  emojis?: string[];
  isStars?: boolean;
  isCumulative?: boolean;
  onChange?: (n: number) => void;
}

const DimRow: React.FC<DimRowProps> = ({
  label, sublabel, value, emojis, isStars, isCumulative = true, onChange,
}) => {
  const [hover, setHover] = useState<number | undefined>(undefined);
  const readonly = !onChange;

  const isActive = (n: number): boolean => {
    const raw = hover ?? value;
    if (raw === undefined) return false;
    const display = Math.round(raw);
    return isCumulative ? display >= n : display === n;
  };

  return (
    <Box>
      <Box sx={{ display: "flex", alignItems: "baseline", gap: 0.75, mb: 0.5 }}>
        <Typography variant="caption" color="text.secondary" fontWeight={600}>
          {label}
        </Typography>
        {sublabel && (
          <Typography variant="caption" color="text.disabled" sx={{ fontSize: 10 }}>
            {sublabel}
          </Typography>
        )}
      </Box>
      <Box sx={{ display: "flex", alignItems: "center", gap: 0.5 }}>
        {[1, 2, 3, 4, 5].map(n => {
          const active = isActive(n);
          return (
            <Box
              key={n}
              onMouseEnter={readonly ? undefined : () => setHover(n)}
              onMouseLeave={readonly ? undefined : () => setHover(undefined)}
              onClick={readonly ? undefined : () => onChange!(n)}
              sx={{
                cursor: readonly ? "default" : "pointer",
                display: "flex",
                alignItems: "center",
                userSelect: "none",
                lineHeight: 0,
              }}
            >
              {isStars ? (
                active
                  ? <Star sx={{ fontSize: 20, color: orange[500] }} />
                  : <StarBorder sx={{ fontSize: 20, color: orange[300] }} />
              ) : (
                <Typography
                  component="span"
                  sx={{
                    fontSize: 20,
                    lineHeight: 1,
                    opacity: value === undefined ? 0.35 : active ? 1 : 0.25,
                    transform: active ? "scale(1.25)" : "scale(1)",
                    transition: "opacity 0.1s, transform 0.1s",
                    display: "inline-block",
                  }}
                >
                  {emojis![n - 1]}
                </Typography>
              )}
            </Box>
          );
        })}
      </Box>
    </Box>
  );
};

// ── Main widget ───────────────────────────────────────────────────────────────

export interface TaskRatingWidgetProps {
  rating: ITaskRating;
  onRate?: (r: ITaskRating) => void;
  label?: string;
}

const TaskRatingWidget: React.FC<TaskRatingWidgetProps> = ({ rating, onRate, label }) => {
  const [anchor, setAnchor] = useState<HTMLElement | null>(null);
  const [localRating, setLocalRating] = useState<ITaskRating>(rating);
  const readonly = !onRate;

  // Keep local state in sync when the parent's rating changes
  useEffect(() => {
    setLocalRating(rating);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [rating.difficulty, rating.fun, rating.frequency, rating.note]);

  const avg = calcTaskRatingAvg(localRating);
  const hasRating = avg !== undefined;
  const hasNote = !!localRating.note?.trim();

  const handleRate = (dim: keyof ITaskRating, n: number) => {
    const next = { ...localRating, [dim]: n };
    setLocalRating(next);
    onRate?.(next);
  };

  const handleNoteChange = (text: string) => {
    const next = { ...localRating, note: text };
    setLocalRating(next);
    onRate?.(next);
  };

  // Render nothing if readonly with no data and no note
  if (readonly && !hasRating && !hasNote) return null;

  const summaryDisplay = hasRating ? (
    <Box sx={{ display: "flex", alignItems: "center", gap: 0.15 }}>
      {[1, 2, 3, 4, 5].map(n =>
        n <= Math.round(avg)
          ? <Star key={n} sx={{ fontSize: 13, color: orange[500] }} />
          : <StarBorder key={n} sx={{ fontSize: 13, color: orange[300] }} />
      )}
    </Box>
  ) : (
    <Typography variant="caption" color="text.disabled">☆</Typography>
  );

  return (
    <>
      <Box
        onClick={e => { e.stopPropagation(); setAnchor(e.currentTarget); }}
        sx={{ display: "inline-flex", alignItems: "center", gap: 0.25, cursor: "pointer" }}
      >
        {label && (
          <Typography variant="caption" color="text.disabled" sx={{ mr: 0.25 }}>
            {label}
          </Typography>
        )}
        {summaryDisplay}
        {hasNote && (
          <ChatBubbleOutline sx={{ fontSize: 11, color: "primary.main", ml: 0.25 }} />
        )}
      </Box>

      <Popover
        open={!!anchor}
        anchorEl={anchor}
        onClose={() => setAnchor(null)}
        anchorOrigin={{ vertical: "bottom", horizontal: "left" }}
        transformOrigin={{ vertical: "top", horizontal: "left" }}
        onClick={e => e.stopPropagation()}
        PaperProps={{ sx: { p: 2 } }}
      >
        <Box sx={{ display: "flex", flexDirection: "column", gap: 1.5, minWidth: 210 }}>
          <DimRow
            label="Difficulty"
            value={localRating.difficulty}
            emojis={DIFFICULTY_EMOJIS}
            isCumulative
            onChange={readonly ? undefined : n => handleRate("difficulty", n)}
          />
          <DimRow
            label="Fun"
            value={localRating.fun}
            emojis={FUN_EMOJIS}
            isCumulative
            onChange={readonly ? undefined : n => handleRate("fun", n)}
          />
          <DimRow
            label="Frequency"
            sublabel="(less often → more often)"
            isStars
            value={localRating.frequency}
            isCumulative
            onChange={readonly ? undefined : n => handleRate("frequency", n)}
          />
          {readonly ? (
            hasNote && (
              <Box sx={{ mt: 0.5, pt: 1, borderTop: "1px solid", borderColor: "divider" }}>
                <Typography variant="caption" color="text.secondary" fontWeight={600} display="block" sx={{ mb: 0.5 }}>
                  Note{localRating.note!.includes("\n") ? "s" : ""}
                </Typography>
                {localRating.note!.split("\n").map((line, i) => (
                  <Typography key={i} variant="caption" color="text.primary" display="block">
                    {line}
                  </Typography>
                ))}
              </Box>
            )
          ) : (
            <TextField
              label="Note"
              placeholder="Optional feedback…"
              multiline
              minRows={2}
              size="small"
              fullWidth
              value={localRating.note ?? ""}
              onChange={e => handleNoteChange(e.target.value)}
              sx={{ mt: 0.5 }}
            />
          )}
        </Box>
      </Popover>
    </>
  );
};

export default TaskRatingWidget;
